import React from "react";
import { Link } from "react-router-dom";
import SiteLayout from "@/components/SiteLayout";
import Icon from "@/components/Icon";
import { useReveal } from "@/hooks/useReveal";
import { ArrowRight, Check } from "lucide-react";
import { OPERATIONAL_ASSESSMENT as OA } from "@/data/evara";

const TABS = [
  { key: "overview", label: "Overview" },
  { key: "methodology", label: "Methodology" },
  { key: "assess", label: "What We Assess" },
  { key: "deliverables", label: "Deliverables & Benefits" },
];

function OverviewPanel() {
  return (
    <div className="fade-up" data-testid="oa-panel-overview">
      <p className="text-[var(--evara-text-2)] leading-relaxed max-w-3xl">{OA.intro}</p>
      <p className="mt-4 font-serif italic text-[var(--evara-primary)] max-w-3xl">{OA.promise}</p>

      <div className="divider-rule my-7" />
      <div className="eyebrow mb-4">Challenges we help solve</div>
      <div className="flex flex-wrap gap-2.5">
        {OA.challenges.map((c) => (
          <span
            key={c}
            className="text-sm px-4 py-2 rounded-full bg-[var(--evara-bg-mint)] text-[var(--evara-primary)] border border-[var(--evara-border)]"
          >
            {c}
          </span>
        ))}
      </div>

      <div className="divider-rule my-7" />
      <div className="eyebrow mb-4">Our assessment framework</div>
      <div className="flex flex-wrap items-center gap-y-3">
        {OA.flow.map((step, i) => (
          <React.Fragment key={step}>
            <span className="text-sm font-medium text-[var(--evara-primary)] bg-[var(--evara-bg-mint)] border border-[var(--evara-border)] rounded-xl px-3.5 py-2 shadow-sm">
              {step}
            </span>
            {i < OA.flow.length - 1 && (
              <ArrowRight size={14} className="mx-2 text-[var(--evara-sage)] shrink-0" />
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

function MethodologyPanel() {
  const [step, setStep] = React.useState(0);
  const current = OA.methodology[step];
  return (
    <div className="grid md:grid-cols-12 gap-6 items-start fade-up" data-testid="oa-panel-methodology">
      <nav className="md:col-span-5 space-y-2">
        {OA.methodology.map((m, i) => {
          const selected = i === step;
          return (
            <button
              key={m.key}
              onClick={() => setStep(i)}
              className={
                "w-full text-left rounded-xl border p-3.5 flex items-center gap-3 transition-all duration-300 " +
                (selected
                  ? "bg-[var(--evara-bg-mint)] border-[rgba(23,105,74,0.45)] shadow-[0_14px_32px_-22px_rgba(23,105,74,0.28)]"
                  : "bg-white/70 border-[var(--evara-border)] hover:border-[var(--evara-sage)]")
              }
              data-testid={`oa-step-${m.key}`}
            >
              <div
                className={
                  "shrink-0 w-9 h-9 rounded-lg flex items-center justify-center " +
                  (selected ? "bg-[var(--evara-primary)] text-white" : "bg-[var(--evara-bg-mint)] text-[var(--evara-primary)]")
                }
              >
                <Icon name={m.icon} size={16} />
              </div>
              <div className="min-w-0">
                <div className="font-serif text-sm md:text-base text-[var(--evara-primary)] leading-snug">{m.title}</div>
              </div>
            </button>
          );
        })}
      </nav>

      <div className="md:col-span-7">
        <div key={current.key} className="card-evara p-7 md:p-8 fade-up" data-testid={`oa-step-panel-${current.key}`}>
          <div className="eyebrow">Step · 0{step + 1}</div>
          <h3 className="font-serif text-2xl md:text-3xl mt-1 text-[var(--evara-primary)]">{current.title}</h3>
          <p className="mt-3 text-[var(--evara-text-2)] text-sm leading-relaxed">{current.blurb}</p>
          <div className="divider-rule my-5" />
          <ul className="grid sm:grid-cols-2 gap-y-2.5 gap-x-6 text-sm">
            {current.items.map((it) => (
              <li key={it} className="flex items-start gap-2.5">
                <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[var(--evara-sage)] shrink-0" />
                <span>{it}</span>
              </li>
            ))}
          </ul>
          <div className="divider-rule my-5" />
          <div className="flex items-center gap-2.5 text-sm">
            <span className="eyebrow">Deliverable</span>
            <span className="font-serif italic text-[var(--evara-primary)]">{current.deliverable}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function AssessPanel() {
  const [area, setArea] = React.useState(0);
  const current = OA.assessAreas[area];
  return (
    <div className="fade-up" data-testid="oa-panel-assess">
      <div className="flex flex-wrap gap-2.5">
        {OA.assessAreas.map((a, i) => {
          const selected = i === area;
          return (
            <button
              key={a.name}
              onClick={() => setArea(i)}
              className={
                "flex items-center gap-2 rounded-full px-4 py-2.5 text-sm border transition-all duration-300 " +
                (selected
                  ? "bg-[var(--evara-primary)] text-white border-[var(--evara-primary)]"
                  : "bg-white/70 text-[var(--evara-primary)] border-[var(--evara-border)] hover:border-[var(--evara-sage)]")
              }
              data-testid={`oa-area-${a.name.toLowerCase()}`}
            >
              <Icon name={a.icon} size={15} />
              {a.name}
            </button>
          );
        })}
      </div>

      <div key={current.name} className="card-evara p-7 md:p-8 mt-6 fade-up" data-testid={`oa-area-panel-${current.name.toLowerCase()}`}>
        <div className="flex items-center gap-4">
          <div className="icon-chip">
            <Icon name={current.icon} size={20} />
          </div>
          <h3 className="font-serif text-2xl md:text-3xl text-[var(--evara-primary)]">{current.name}</h3>
        </div>
        <div className="divider-rule my-6" />
        <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-y-3 gap-x-6 text-sm">
          {current.items.map((it) => (
            <li key={it} className="flex items-start gap-2.5">
              <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[var(--evara-sage)] shrink-0" />
              <span>{it}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function DeliverablesPanel() {
  return (
    <div className="fade-up" data-testid="oa-panel-deliverables">
      <div className="grid md:grid-cols-2 gap-6">
        <div className="card-evara p-7">
          <div className="eyebrow mb-4">What you receive</div>
          <ul className="grid gap-y-2.5 text-sm">
            {OA.deliverables.map((d) => (
              <li key={d} className="flex items-start gap-2.5">
                <Check size={15} className="mt-0.5 text-[var(--evara-primary)] shrink-0" />
                <span>{d}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="card-evara p-7">
          <div className="eyebrow mb-4">Benefits you can expect</div>
          <ul className="grid gap-y-2.5 text-sm">
            {OA.benefits.map((b) => (
              <li key={b} className="flex items-start gap-2.5">
                <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[var(--evara-sage)] shrink-0" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="card-evara p-7 mt-6 overflow-x-auto">
        <div className="eyebrow mb-4">The assessment at a glance</div>
        <table className="w-full text-sm" data-testid="oa-glance-table">
          <thead>
            <tr className="text-left text-[var(--evara-muted)] border-b border-[var(--evara-border)]">
              <th className="py-2.5 pr-4 font-medium">Phase</th>
              <th className="py-2.5 pr-4 font-medium">Activities</th>
              <th className="py-2.5 font-medium">Deliverables</th>
            </tr>
          </thead>
          <tbody>
            {OA.glance.map((row) => (
              <tr key={row.phase} className="border-b border-[var(--evara-border)] last:border-0">
                <td className="py-2.5 pr-4 font-serif text-[var(--evara-primary)]">{row.phase}</td>
                <td className="py-2.5 pr-4 text-[var(--evara-text-2)]">{row.activities}</td>
                <td className="py-2.5 text-[var(--evara-text-2)]">{row.deliverable}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function OperationalAssessment() {
  useReveal();
  const [tab, setTab] = React.useState("overview");

  return (
    <SiteLayout>
      <section className="section-pad bg-sage-gradient pb-10" data-testid="oa-hero">
        <div className="max-w-6xl mx-auto">
          <div className="eyebrow">Flagship Diagnostic</div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-5 leading-[1.05]">
            Operational Assessment.<br />
            <em className="not-italic text-[var(--evara-primary)] font-medium">{OA.tagline}</em>
          </h1>
          <p className="mt-6 text-lg text-[var(--evara-text-2)] max-w-3xl leading-relaxed">
            A structured, evidence-based evaluation of your operations. Pick a section to explore.
          </p>
        </div>
      </section>

      <section className="section-pad pt-8" data-testid="oa-tabs-section">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-wrap gap-2.5 border-b border-[var(--evara-border)] pb-5" role="tablist">
            {TABS.map((t) => {
              const selected = t.key === tab;
              return (
                <button
                  key={t.key}
                  role="tab"
                  aria-selected={selected}
                  onClick={() => setTab(t.key)}
                  className={
                    "rounded-full px-5 py-2.5 text-sm border transition-all duration-300 " +
                    (selected
                      ? "bg-[var(--evara-primary)] text-white border-[var(--evara-primary)] shadow-[0_12px_28px_-18px_rgba(23,105,74,0.45)]"
                      : "bg-white/70 text-[var(--evara-primary)] border-[var(--evara-border)] hover:border-[var(--evara-sage)]")
                  }
                  data-testid={`oa-tab-${t.key}`}
                >
                  {t.label}
                </button>
              );
            })}
          </div>

          <div className="mt-8">
            {tab === "overview" && <OverviewPanel />}
            {tab === "methodology" && <MethodologyPanel />}
            {tab === "assess" && <AssessPanel />}
            {tab === "deliverables" && <DeliverablesPanel />}
          </div>

          <div className="mt-10 flex flex-wrap items-center justify-between gap-3 border-t border-[var(--evara-border)] pt-6">
            <p className="text-xs text-[var(--evara-muted)]">
              Ready to see where your operations stand?
            </p>
            <Link to="/book" className="btn-primary" data-testid="oa-cta-btn">
              Schedule an Operational Assessment <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
